import React, { useState } from 'react';
import { Box, Grid, Typography, FormControlLabel, Switch } from '@material-ui/core';
import i18n from 'i18n-js';
import useStyle from './RulesModal.styled';
import { Field } from 'formik';
import InputAdornment from '@material-ui/core/InputAdornment';
import { TextField } from 'components/FormFields';
import Tooltip from '@material-ui/core/Tooltip';
import HelpIcon from '@material-ui/icons/Help';
import { PricingOperatorSelect, DaysSelect } from 'components/DataFields';
import { SwitchField, DateTimePickerField } from 'components/FormFields';
import { EditableTable } from 'components/UI';
import { formatAmount } from 'utils';
import * as Yup from 'yup';
import { parseInputNumber } from 'utils';
import { TimeFrameSelect } from 'components/DataFields';
import { TimeFrameCalculation } from './TimeFrameCalculation';
import { useSelector } from 'react-redux';

const periodSchema = Yup.object().shape({
  period: Yup.number()
    .required(i18n.t('common.required_field'))
    .typeError(i18n.t('ruleset.rate_error')),
  rate: Yup.number()
    .required(i18n.t('common.required_field'))
    .typeError(i18n.t('ruleset.rate_error')),
});

const PriceTypeToInput = ({ pricingTypeName, values, units, formRef, showRateLimit }) => {
  const classes = useStyle();
  const [errorMsg, setErrorMsg] = useState(undefined);

  switch (pricingTypeName) {
    case 'RETURNEDLATE':
      return (
        <>
          <Grid item xs={12} className={classes.mt}>
            <Typography variant="h6">
              <Box
                fontWeight="fontWeightBold"
                fontSize={14}
                alignItems="center"
                display="flex"
                style={{ height: '100%' }}
              >
                {i18n.t('ruleset.max_time')}
                <Tooltip
                  title={i18n.t('ruleset.max_time_description')}
                  placement="top"
                  aria-label="help"
                >
                  <HelpIcon color="primary" className={classes.helpIcon} />
                </Tooltip>
              </Box>
            </Typography>
            <Grid item md={6} className={classes.mt}>
              <Grid item>
                <Field
                  type="number"
                  margin="none"
                  name="extra_arguments.max_time_to_bill"
                  component={TextField}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.inputAdornment,
                        }}
                        position="end"
                      >
                        {units?.total || ''}
                      </InputAdornment>
                    ),
                  }}
                  label={i18n.t('ruleset.enter_max_time')}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} className={classes.mt}>
            <Typography variant="h6">
              <Box
                fontWeight="fontWeightBold"
                fontSize={14}
                display="flex"
                alignItems="center"
                style={{ height: '100%' }}
              >
                {i18n.t('ruleset.grace_period')}
                <Tooltip
                  placement="top"
                  title={i18n.t('ruleset.grace_period_description')}
                  aria-label="help"
                >
                  <HelpIcon color="primary" className={classes.helpIcon} />
                </Tooltip>
              </Box>
            </Typography>
            <Grid item md={6} className={classes.mt}>
              <Grid item>
                <Field
                  type="number"
                  name="extra_arguments.grace_period"
                  fontSize={14}
                  margin="none"
                  inputProps={{
                    endAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.inputAdornment,
                        }}
                        position="end"
                      >
                        {units?.total || ''}
                      </InputAdornment>
                    ),
                  }}
                  component={TextField}
                  label={i18n.t('ruleset.enter_grace_period')}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      );
    case 'RETURNENERGYLEVEL':
      return (
        <>
          <Grid item xs={12} className={classes.mt}>
            <Typography variant="h6">
              <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                {i18n.t('ruleset.operator')}
              </Box>
            </Typography>
            <Grid item md={6} className={classes.mt}>
              <Grid item>
                <Field
                  margin="none"
                  name="extra_arguments.operator"
                  component={PricingOperatorSelect}
                  label={i18n.t('ruleset.operator')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.mt}>
            <Typography variant="h6">
              <Box
                fontWeight="fontWeightBold"
                fontSize={14}
                display="flex"
                alignItems="center"
                style={{ height: '100%' }}
              >
                {i18n.t('ruleset.level_pct')}
                <Tooltip
                  placement="top"
                  title={i18n.t('ruleset.level_pct_description')}
                  aria-label="help"
                >
                  <HelpIcon color="primary" className={classes.helpIcon} />
                </Tooltip>
              </Box>
            </Typography>
            <Grid item md={6} className={classes.mt}>
              <Grid item>
                <Field
                  type="number"
                  margin="none"
                  name="extra_arguments.level_pct"
                  component={TextField}
                  label={i18n.t('ruleset.enter_level_pct')}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      );
    case 'SUBSCRIPTIONPERIODFACTOR':
      return (
        <Grid item xs={12} className={classes.mt}>
          <Typography variant="h6">
            <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
              {i18n.t('ruleset.financing_factor')}
            </Box>
          </Typography>
          <Grid item md={6} className={classes.mt}>
            <Grid item>
              <Field
                type="number"
                name="extra_arguments.financing_factor"
                component={TextField}
                label={i18n.t('ruleset.enter_financing_factor')}
              />
            </Grid>
          </Grid>
        </Grid>
      );
    case 'SUBSCRIPTIONDELIVERYSTATE':
      return (
        <Grid item xs={12} className={classes.mt}>
          <Typography variant="h6">
            <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
              {i18n.t('ruleset.out_of_state')}
            </Box>
          </Typography>
          <Grid item md={6} className={classes.mt}>
            <Grid item>
              <Field
                type="number"
                name="extra_arguments.out_of_state"
                component={TextField}
                label={i18n.t('ruleset.enter_out_of_state')}
              />
            </Grid>
          </Grid>
        </Grid>
      );
    case 'SUBSCRIPTIONPERIODTIERED':
    case 'SUBSCRIPTIONDEPOSITTIERED':
      const data = Object.entries(values?.extra_arguments?.tiers).map((val) => ({
        period: val[0],
        rate: formatAmount(val[1]),
      }));
      const columns = [
        {
          title: i18n.t('ruleset.periods'),
          field: 'period',
        },
        {
          title: i18n.t('ruleset.rate'),
          field: 'rate',
          render: (rowData) => <span>{`$${rowData.rate}`}</span>,
        },
      ];
      return (
        <>
          <EditableTable
            addButtonProps={{
              variant: 'outlined',
              position: 'flex-end',
              dataCy: 'add-new-tier',
              label: i18n.t('ruleset.add_new_tier'),
            }}
            requiredFields={['period', 'rate']}
            columns={columns}
            data={data}
            onRowAdd={(value, resolve, reject) => {
              periodSchema
                .validate(value)
                .then(function (value) {
                  setErrorMsg(undefined);
                  const newTiers = {
                    ...formRef.current.values?.extra_arguments?.tiers,
                    [value.period]: parseInputNumber(value.rate),
                  };
                  formRef.current.setFieldValue('extra_arguments.tiers', newTiers);
                  resolve();
                })
                .catch(function (err) {
                  setErrorMsg(err.message);
                  reject();
                });
            }}
            onDelete={(value, resolve, reject) => {
              periodSchema
                .validate(value)
                .then(function (value) {
                  setErrorMsg(undefined);
                  const newTiers = {
                    ...formRef?.current.values?.extra_arguments?.tiers,
                  };
                  delete newTiers[value.period];
                  formRef.current.setFieldValue('extra_arguments.tiers', newTiers);
                  resolve();
                })
                .catch(function (err) {
                  setErrorMsg(err.message);
                  reject();
                });
            }}
            onEdit={(value, resolve, reject, oldData) => {
              periodSchema
                .validate(value)
                .then(function (value) {
                  setErrorMsg(undefined);
                  const newTiers = {
                    ...formRef?.current.values?.extra_arguments?.tiers,
                    [value.period]: parseInputNumber(value.rate),
                  };
                  if (+oldData.period !== +value.period) {
                    delete newTiers[oldData.period];
                  }
                  formRef.current.setFieldValue('extra_arguments.tiers', newTiers);
                  resolve();
                })
                .catch(function (err) {
                  setErrorMsg(err.message);
                  reject();
                });
            }}
            onRowAddCancelled={() => setErrorMsg(undefined)}
            onRowUpdateCancelled={() => setErrorMsg(undefined)}
          />
          {errorMsg && <span className={classes.tableError}>{errorMsg}</span>}
        </>
      );
    case 'DISTANCEDRIVEN':
      const selected_timeframe = values?.extra_arguments?.timeframe;
      const distance_allowance = values?.extra_arguments?.distance_allowance;

      return !showRateLimit ? (
        <Grid item xs={12} className={classes.mt}>
          <Typography variant="h6">
            <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
              {i18n.t('ruleset.distance_allowance')}
            </Box>
          </Typography>
          <Grid item xs={12}>
            <Typography variant="p">
              <Box fontSize={13} style={{ height: '100%' }}>
                {i18n.t('ruleset.distance_allowance_description')}
              </Box>
            </Typography>
            <Grid
              container
              spacing={2}
              xs={12}
              style={{
                justifyContent: 'space-between',
              }}
            >
              <Grid item xs={6}>
                <Field
                  type="number"
                  name="extra_arguments.distance_allowance"
                  disabled={!units}
                  required={false}
                  onChange={(val) => {
                    const inputValue = val.target.value;
                    const sanitizedValue = parseInt(inputValue || '0', 10).toString();
                    if (sanitizedValue === '' || sanitizedValue === '0') {
                      formRef.current.setFieldValue('extra_arguments.distance_allowance', 0);
                      formRef.current.setFieldValue('extra_arguments.acceptable_excess', 0);
                    }
                    formRef.current.setFieldValue(
                      'extra_arguments.distance_allowance',
                      sanitizedValue
                    );
                  }}
                  inputProps={{
                    step: 1,
                    min: 0,
                    endAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.inputAdornment,
                        }}
                        position="end"
                      >
                        {distance_allowance > 1 ||
                        distance_allowance == 0 ||
                        distance_allowance === ''
                          ? units?.total
                          : units?.one || ''}
                      </InputAdornment>
                    ),
                  }}
                  component={TextField}
                  label={i18n.t('ruleset.included_units', {
                    units: units?.total || 'miles',
                  })}
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  type="text"
                  disabled={!units}
                  handleChange={(e) => onChange(e)}
                  required={false}
                  name="extra_arguments.timeframe"
                  onChange={(val) => {
                    if (val) {
                      formRef.current.setFieldValue('extra_arguments.timeframe', val.target.value);
                      if (val.target.value === '') {
                        formRef.current.setFieldValue('extra_arguments.distance_allowance', 0);
                        formRef.current.setFieldValue('extra_arguments.timeframe', '');
                      }
                    }
                  }}
                  component={TimeFrameSelect}
                  label={i18n.t('ruleset.timeframe')}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} className={classes.mt}>
              <Typography variant="p">
                <Box
                  fontStyle="italic"
                  fontSize={12}
                  fontWeight="fontWeightRegular"
                  lineHeight="19px"
                >
                  {selected_timeframe && units?.total ? (
                    <TimeFrameCalculation
                      selected_timeframe={selected_timeframe}
                      distance_allowance={distance_allowance}
                      units={units}
                    />
                  ) : (
                    <br />
                  )}
                </Box>
              </Typography>
            </Grid>
            <Grid
              container
              spacing={2}
              xs={12}
              style={{
                justifyContent: 'space-between',
              }}
            >
              <Grid item xs={6}>
                <Field
                  type="number"
                  name="unit_price"
                  disabled={!units}
                  format="price"
                  required={true}
                  component={TextField}
                  label={i18n.t('ruleset.rate_per_extra_unit', { unit: units?.one || 'mile' })}
                  startAdornment={
                    <InputAdornment position="start" style={{ marginRight: -8 }}>
                      $
                    </InputAdornment>
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  type="number"
                  disabled={
                    !values.extra_arguments.distance_allowance ||
                    values.extra_arguments.distance_allowance < 1 ||
                    !units
                  }
                  required={false}
                  name="extra_arguments.acceptable_excess"
                  onChange={(val) => {
                    const inputValue = val.target.value;
                    const sanitizedValue = parseInt(inputValue || '0', 10).toString();
                    if (sanitizedValue === '') {
                      formRef.current.setFieldValue('extra_arguments.acceptable_excess', 0);
                    }
                    formRef.current.setFieldValue(
                      'extra_arguments.acceptable_excess',
                      sanitizedValue
                    );
                  }}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.inputAdornment,
                        }}
                        position="end"
                      >
                        {values?.extra_arguments?.acceptable_excess != null
                          ? values?.extra_arguments?.acceptable_excess == 1
                            ? units?.one
                            : units?.total || ''
                          : ''}
                      </InputAdornment>
                    ),
                  }}
                  component={TextField}
                  label={i18n.t('ruleset.reservation_acceptance_excess')}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : null;
    default:
      return <></>;
  }
};

const RateInput = ({
  values,
  units,
  formRef,
  setFieldValue,
  combosAllowed,
  useAdditionalMiles,
  setAdditionalMiles,
}) => {
  const classes = useStyle();
  const pricingTypeName = values?.pricing_type?.name;
  const isTiered =
    pricingTypeName === 'SUBSCRIPTIONPERIODTIERED' ||
    pricingTypeName === 'SUBSCRIPTIONDEPOSITTIERED';

  const allowAdditionalMilesEnabled = useSelector(
    (state) => state.config?.settings?.core?.pricing?.allow_additional_miles || false
  );

  const showRateLimit =
    values?.pricing_type && (values?.pricing_type?.name !== 'DISTANCEDRIVEN' || useAdditionalMiles);

  const handleUseAdditionalMilesChange = (value) => {
    if (!value) {
      formRef.current.setFieldValue('extra_arguments', {});
    }
    setAdditionalMiles(value);
  };

  return (
    <>
      {allowAdditionalMilesEnabled &&
      values?.pricing_type?.name === 'DISTANCEDRIVEN' &&
      values?.invoice_item_type_id ? (
        <Grid item xs={12} className={classes.rowContainer}>
          <Grid item xs={10}>
            <Typography variant="h6">
              <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                {i18n.t('ruleset.use_distance_allowance')}
              </Box>
            </Typography>
            <Typography variant="h6">
              <Box fontWeight="400" fontSize={13} style={{ height: '100%', opacity: 0.8 }}>
                {i18n.t('ruleset.use_distance_allowance_description')}
              </Box>
            </Typography>
          </Grid>

          <Grid item xs={2} className={classes.radioRow}>
            <Switch
              onChange={(_, value) => handleUseAdditionalMilesChange(value)}
              checked={useAdditionalMiles}
              name="active"
              color="primary"
            />
          </Grid>
        </Grid>
      ) : null}
      {(!showRateLimit || values?.pricing_type?.name !== 'DISTANCEDRIVEN') && (
        <>
          <Grid item xs={12} className={classes.mt}>
            <Typography variant="h6">
              <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                {i18n.t('ruleset.rate')}
              </Box>
            </Typography>
            <Grid item md={6}>
              <Grid item>
                <Field
                  type="number"
                  name="unit_price"
                  inputProps={{
                    endAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.inputAdornment,
                        }}
                        position="end"
                      >
                        {units?.per_unit ? `$${units?.per_unit}` : ''}
                      </InputAdornment>
                    ),
                  }}
                  component={TextField}
                  label={i18n.t('ruleset.enter_rate')}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      <PriceTypeToInput
        pricingTypeName={pricingTypeName}
        values={values}
        units={units}
        formRef={formRef}
        showRateLimit={!showRateLimit}
      />

      {!isTiered && combosAllowed && (
        <>
          <Grid item xs={12}>
            <Typography variant="h6">
              <Box fontWeight="fontWeightBold" fontSize={14} mt={3} style={{ height: '100%' }}>
                {i18n.t('ruleset.schedule_time')}
              </Box>
            </Typography>
          </Grid>

          <Grid item xs={12} className={classes.rowContainer}>
            <Grid container spacing={2}>
              <Grid item xs={10}>
                <Typography variant="h6">
                  <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                    {i18n.t('ruleset.all_time_title')}
                  </Box>
                </Typography>
                <Typography variant="h6">
                  <Box fontWeight="400" fontSize={13} style={{ height: '100%', opacity: 0.8 }}>
                    {i18n.t('ruleset.all_time_description')}
                  </Box>
                </Typography>
              </Grid>

              <Grid item xs={2} className={classes.radioRow}>
                <Field
                  name="all_time"
                  setFieldValue={setFieldValue}
                  onChange={(val) => {
                    if (val) {
                      formRef.current.setFieldValue('start', 1);
                      formRef.current.setFieldValue('end', 8);
                    }
                  }}
                  color="primary"
                  component={SwitchField}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      {!values?.all_time && (
        <>
          <Grid item xs={12} className={classes.mt}>
            <Grid item xs={12} className={classes.groupForm}>
              <Grid item xs={6}>
                <Typography variant="h6">
                  <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                    {i18n.t('ruleset.start_day')}
                  </Box>
                </Typography>
                <div
                  style={{
                    maxWidth: '93%',
                  }}
                >
                  <Field
                    name="start_day"
                    component={DaysSelect}
                    label={i18n.t('ruleset.select_day')}
                  />
                </div>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6">
                  <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                    {i18n.t('ruleset.start_hour')}
                  </Box>
                </Typography>
                <div
                  style={{
                    maxWidth: '93%',
                  }}
                >
                  <Field
                    utc={true}
                    editable={true}
                    name="start"
                    minDate={new Date()}
                    showTimeSelect={true}
                    showTimeSelectOnly={true}
                    time={true}
                    hours={true}
                    day={values?.start_day || 0}
                    popperPlacement="bottom-start"
                    dateFormat="h:mm aa"
                    className={classes.timePicker}
                    timeFormat="h:mm aa"
                    timeIntervals={60}
                    component={DateTimePickerField}
                    label={i18n.t('ruleset.select_hour')}
                  />
                </div>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.mt}>
            <Grid item xs={12} className={classes.endWeek}>
              <Field
                name="end_week"
                setFieldValue={setFieldValue}
                onChange={(val) => {
                  if (val) {
                    formRef.current.setFieldValue('end', 8);
                  }
                  if (!val) {
                    formRef.current.setFieldValue('end', '');
                  }
                }}
                label={i18n.t('ruleset.until_end_week')}
                color="primary"
                component={SwitchField}
              />
            </Grid>
            {!values?.end_week && (
              <Grid item xs={12} className={classes.groupForm}>
                <Grid item xs={6}>
                  <Typography variant="h6">
                    <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                      {i18n.t('ruleset.end_day')}
                    </Box>
                  </Typography>
                  <div
                    style={{
                      maxWidth: '93%',
                    }}
                  >
                    <Field
                      name="end_day"
                      component={DaysSelect}
                      label={i18n.t('ruleset.select_day')}
                    />
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6">
                    <Box fontWeight="fontWeightBold" fontSize={14} style={{ height: '100%' }}>
                      {i18n.t('ruleset.end_hour')}
                    </Box>
                  </Typography>
                  <div
                    style={{
                      maxWidth: '93%',
                    }}
                  >
                    <Field
                      utc={true}
                      name="end"
                      hours={true}
                      editable={true}
                      time={true}
                      style={{
                        maxWidth: '93%',
                      }}
                      className={classes.timePicker}
                      day={values?.end_day || 0}
                      minDate={new Date()}
                      showTimeSelect={true}
                      showTimeSelectOnly={true}
                      popperPlacement="bottom-start"
                      dateFormat="h:mm aa"
                      timeFormat="h:mm aa"
                      timeIntervals={60}
                      component={DateTimePickerField}
                      label={i18n.t('ruleset.select_hour')}
                    />
                  </div>
                </Grid>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );
};

export { RateInput };
